import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { Sparkles, Download, Share2, RotateCcw, Plus, Lock, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Navbar } from '@/components/Navbar';
import EmailCaptureModal from '@/components/EmailCaptureModal';
import { splitStoryAtWords, highlightStoryTokens } from '@/utils/storyHighlights';
import { usePricing } from '@/contexts/PricingContext';
import { generateStoryPdf } from '@/utils/pdfGenerator';
import { buildWhatsAppShareUrl, buildStoryUrl } from '@/utils/whatsappShare';
import {
  createCheckout, verifyPayment, generateStory, fetchStoryById, getPaymentStatus,
} from '@/utils/api';
import { trackStoryGenerated, trackPaymentComplete } from '@/utils/analytics';
import { toast } from 'sonner';
import { MYTHOLOGIES } from '@/utils/mythologies';

export default function StoryOutput() {
  const navigate = useNavigate();
  const { storyId } = useParams();
  const [searchParams] = useSearchParams();
  const unlockToken = searchParams.get('unlock'); // Dodo session_id, either from email or return_url
  const returnStatus = searchParams.get('status'); // 'success' | 'cancelled' from Dodo return_url
  const { loading: priceLoading, pricing, country } = usePricing();

  const [story, setStory] = useState(null);
  const [input, setInput] = useState(null);
  const [loadingStory, setLoadingStory] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const [unlocked, setUnlocked] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [gaTracked, setGaTracked] = useState(false); // avoid double-firing payment_complete GA event

  // -------- Hydrate story: prefer sessionStorage cache, else fetch by :storyId --------
  useEffect(() => {
    let mounted = true;
    const rawSession = sessionStorage.getItem('tl:currentStory');

    // Try session cache first (matches storyId if URL has one)
    if (rawSession) {
      try {
        const parsed = JSON.parse(rawSession);
        if (!storyId || parsed?.story?.id === storyId) {
          if (mounted) {
            setStory(parsed.story);
            setInput(parsed.input);
          }
          return () => { mounted = false; };
        }
      } catch (_) { /* fall through */ }
    }

    // No session match — need a storyId to fetch
    if (!storyId) {
      navigate('/create');
      return () => { mounted = false; };
    }

    setLoadingStory(true);
    fetchStoryById(storyId)
      .then((s) => {
        if (!mounted) return;
        setStory(s);
        setInput({
          child_name: s.child_name,
          mythology_name: s.mythology,
          deity_name: s.deity,
          language: s.language,
          age: s.age,
          gender: s.gender,
          interest_1: s.interest_1,
          interest_2: s.interest_2,
          sibling: s.sibling,
        });
      })
      .catch(() => {
        if (!mounted) return;
        setNotFound(true);
      })
      .finally(() => {
        if (mounted) setLoadingStory(false);
      });

    return () => { mounted = false; };
  }, [storyId, navigate]);

  // -------- Verify unlock token (from email link OR Dodo return_url) --------
  useEffect(() => {
    if (!unlockToken || !story || unlocked) return;
    let cancelled = false;
    let cancelledStatus = returnStatus === 'cancelled';

    if (cancelledStatus) {
      toast.info('Payment cancelled. You can retry anytime.', { duration: 5000 });
      return () => { cancelled = true; };
    }

    async function verifyAndPoll() {
      setVerifying(true);
      // Poll up to 60s (webhook usually lands within a few seconds after Dodo redirect)
      const deadline = Date.now() + 60000;
      let lastResult = null;
      while (!cancelled && Date.now() < deadline) {
        try {
          const r = await verifyPayment(unlockToken);
          lastResult = r;
          if (r.paid && (!r.story_id || r.story_id === story.id)) {
            if (cancelled) return;
            setUnlocked(true);
            if (!gaTracked) {
              trackPaymentComplete({
                checkoutId: unlockToken,
                storyId: story.id,
                plan: 'single',
                amount: pricing?.single?.amount,
                currency: pricing?.single?.currency,
                childName: input?.child_name,
              });
              setGaTracked(true);
            }
            const emailSent = r?.email?.sent;
            if (returnStatus === 'success') {
              toast.success(
                emailSent
                  ? 'Payment successful — story unlocked. Check your email!'
                  : 'Payment successful — story unlocked!'
              );
            }
            return;
          }
        } catch (e) {
          // Backend might not have the checkout yet (race). Keep polling briefly.
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 3000));
      }
      if (!cancelled) {
        if (returnStatus === 'success') {
          toast.error(
            (lastResult && lastResult.status === 'paid')
              ? 'Payment confirmed but story is still loading. Please refresh in a moment.'
              : 'Payment is still processing — refresh in a minute to unlock.',
            { duration: 8000 }
          );
        }
      }
    }

    verifyAndPoll().finally(() => { if (!cancelled) setVerifying(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlockToken, story, returnStatus]);

  const shareUrl = story ? buildStoryUrl(story.id) : null;

  const { first, second } = useMemo(() => {
    if (!story) return { first: '', second: '' };
    return splitStoryAtWords(story.body, 200);
  }, [story]);

  const meta = useMemo(() => (
    input ? {
      childName: input.child_name,
      deityName: input.deity_name,
      interests: [input.interest_1, input.interest_2].filter(Boolean),
      siblingName: input.sibling?.name,
    } : null
  ), [input]);

  const firstHtml = useMemo(() => (first && meta ? highlightStoryTokens(first, meta) : ''), [first, meta]);
  const secondHtml = useMemo(() => (second && meta ? highlightStoryTokens(second, meta) : ''), [second, meta]);

  const chosenMyth = MYTHOLOGIES.find((m) => m.key === input?.mythology_name);

  const openEmailModal = () => {
    if (priceLoading) return;
    setEmailModalOpen(true);
  };

  const doCheckoutWithLead = useCallback(async ({ parent_name, parent_email }) => {
    setPaying(true);
    try {
      // Detect user's country from the pricing context so Dodo gets a valid ISO alpha-2
      const cc = (country && country !== 'DEFAULT') ? country : 'US';
      const checkout = await createCheckout({
        plan: 'single',
        amount: pricing.single.amount,
        currency: pricing.single.currency,
        country: cc,
        child_name: input.child_name,
        story_id: story.id,
        story_title: story.title,
        parent_name,
        parent_email,
        return_origin: window.location.origin,
      });
      // Persist lead locally so if the user returns via Dodo redirect on a fresh tab we can still show it
      try {
        sessionStorage.setItem('tl:pendingCheckout', JSON.stringify({
          checkout_id: checkout.checkout_id,
          story_id: story.id,
        }));
      } catch (_) { /* ignore */ }
      // Redirect to Dodo hosted checkout
      window.location.assign(checkout.checkout_url);
    } catch (e) {
      setPaying(false);
      toast.error(e?.userMessage || 'Payment initialization failed. Please try again.', { duration: 6000 });
    }
  }, [pricing, input, story, country]);

  const doDownloadPdf = () => {
    try {
      generateStoryPdf({
        childName: input.child_name,
        mythology: input.mythology_name,
        title: story.title,
        body: story.body,
      });
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('PDF generation failed.');
    }
  };

  const doWhatsApp = () => {
    const url = buildWhatsAppShareUrl({
      childName: input.child_name,
      storyTitle: story.title,
      storyUrl: shareUrl,
    });
    window.open(url, '_blank');
  };

  const doRegenerate = async () => {
    setRegenerating(true);
    try {
      const payload = {
        child_name: input.child_name,
        age: input.age,
        gender: input.gender,
        interest_1: input.interest_1,
        interest_2: input.interest_2,
        mythology_name: input.mythology_name,
        deity_name: input.deity_name,
        language: input.language,
        word_count: 1000,
      };
      if (input.sibling) payload.sibling = input.sibling;
      const pending = await generateStory(payload);
      // Poll until backend finishes
      const start = Date.now();
      let fresh = pending;
      while (Date.now() - start < 180000) {
        // eslint-disable-next-line no-await-in-loop
        fresh = await fetchStoryById(pending.id);
        if (fresh.status === 'ready' && fresh.body) break;
        if (fresh.status === 'error') throw new Error(fresh.error || 'Regeneration failed');
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 2500));
      }
      if (fresh.status !== 'ready') throw new Error('Regeneration timed out');
      // GA4: story_generated on regenerate too
      trackStoryGenerated({
        storyId: fresh.id,
        childName: fresh.child_name || payload.child_name,
        mythology: fresh.mythology || payload.mythology_name,
        deity: fresh.deity || payload.deity_name,
        language: fresh.language || payload.language,
        wordCount: fresh.word_count,
        hasSibling: !!payload.sibling,
      });
      sessionStorage.setItem('tl:currentStory', JSON.stringify({ story: fresh, input: payload }));
      navigate(`/story/${fresh.id}`);
      setStory(fresh);
      setInput(payload);
      toast.success('Fresh story generated!');
    } catch (e) {
      toast.error(e?.userMessage || e?.message || 'Failed to generate new story.', { duration: 6000 });
    } finally {
      setRegenerating(false);
    }
  };

  // ---------------- LOADING / NOT FOUND ----------------
  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar variant="light" />
        <div className="max-w-xl mx-auto px-6 pt-16 text-center">
          <div className="text-[42px]">🌙</div>
          <h1 className="mt-3 text-[24px] font-semibold text-slate-900">Story not found</h1>
          <p className="mt-2 text-[13px] text-slate-600">This story link may be invalid or expired.</p>
          <Link to="/create" className="mt-6 inline-block">
            <Button className="bg-tl-purple hover:bg-tl-purpleDark text-white h-11 px-5">
              Create a new story <Sparkles className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!story || loadingStory) {
    return (
      <div className="min-h-screen bg-white">
        <div className="bg-tl-hero"><Navbar variant="dark" /></div>
        <div className="max-w-2xl mx-auto px-6 py-16 space-y-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-1/3" />
          <div className="pt-4 space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-11/12" />
            <Skeleton className="h-4 w-10/12" />
            <Skeleton className="h-4 w-11/12" />
            <Skeleton className="h-4 w-9/12" />
          </div>
        </div>
      </div>
    );
  }

  // ---------------- MAIN RENDER ----------------
  return (
    <div className="min-h-screen bg-white">
      {/* Dark header */}
      <section className="bg-tl-hero text-white">
        <Navbar variant="dark" />
        <div data-testid="story-header" className="max-w-3xl mx-auto px-4 sm:px-6 pt-2 pb-10">
          <div data-testid="story-mythology-badge" className="inline-flex items-center gap-2 rounded-full border border-tl-purple/50 bg-tl-purple/20 px-3 py-1.5 text-[11px] text-tl-textSecondaryOnDark">
            <Sparkles className="h-3 w-3 text-tl-gold" />
            {input.mythology_name} mythology · {input.language} · personalized for {input.child_name}, {input.age}
          </div>
          <h1 data-testid="story-title" className="mt-4 text-[26px] sm:text-[32px] font-semibold text-white font-story leading-tight">
            {story.title}
          </h1>
          <div className="mt-2 text-[12px] text-tl-textSecondaryOnDark">
            A bedtime story with {input.deity_name} · every story is unique
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-10 sm:py-14">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          {/* First (free) portion */}
          <div
            data-testid="story-body"
            className="font-story text-[15px] leading-[1.9] text-slate-800 whitespace-pre-line"
            dangerouslySetInnerHTML={{ __html: firstHtml }}
          />

          {/* Pre-paywall WhatsApp share — always visible */}
          <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-5">
            <Button
              data-testid="story-whatsapp-share-pre"
              onClick={doWhatsApp}
              className="h-11 bg-[#25D366] hover:bg-[#1EBE5B] text-white text-[13px] gap-2 shadow-sm"
            >
              <Share2 className="h-4 w-4" /> Share on WhatsApp
            </Button>
            <span className="text-[11px] text-slate-500">Send this to a friend or family — they'll see the free preview too.</span>
          </div>

          {/* Second portion — blurred unless unlocked */}
          <div className="mt-6 relative" data-testid="story-paywall-blur-region">
            <div
              className={`font-story text-[15px] leading-[1.9] text-slate-800 whitespace-pre-line ${!unlocked ? 'select-none' : ''}`}
              style={!unlocked ? { filter: 'blur(4px)', pointerEvents: 'none', minHeight: '260px' } : undefined}
              aria-hidden={!unlocked}
              dangerouslySetInnerHTML={{ __html: secondHtml }}
            />

            {!unlocked && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px]" aria-hidden="true" />
                <div data-testid="story-unlock-card" className="relative w-[92%] max-w-md bg-white border border-tl-border/40 rounded-tlLg shadow-tl-float p-5 sm:p-6">
                  <div className="flex items-center gap-2 text-tl-purple">
                    <Lock className="h-4 w-4" />
                    <span className="text-[11px] uppercase tracking-[0.14em]">
                      {verifying ? 'Confirming your payment…' : 'Unlock the complete story'}
                    </span>
                  </div>
                  {verifying ? (
                    <p className="mt-3 text-[13px] leading-6 text-slate-700">
                      Payment received. Waiting for confirmation from Dodo Payments — this usually takes a few seconds.
                    </p>
                  ) : (
                    <p className="mt-3 text-[13px] leading-6 text-slate-700">
                      Read {input.child_name}'s full adventure with {input.deity_name} and get a beautiful PDF emailed to you.
                    </p>
                  )}
                  <Button
                    data-testid="story-unlock-button"
                    onClick={openEmailModal}
                    disabled={priceLoading || paying || verifying}
                    className="w-full mt-4 h-12 bg-tl-purple hover:bg-tl-purpleDark text-white text-[13px] gap-2 disabled:opacity-60"
                  >
                    {paying ? 'Opening checkout…' : verifying ? 'Verifying…' : (
                      <>Unlock full story — <span data-testid="story-geo-price">{priceLoading ? '…' : pricing.single.display}</span></>
                    )}
                  </Button>
                  <div className="mt-3 text-center text-[11px] text-slate-500">
                    Or get unlimited mythology stories from {priceLoading ? '…' : pricing.family.display}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Below payment gate: create-your-own CTA (shown to anyone who lands on shared URL) */}
          {!unlocked && (
            <div data-testid="story-create-your-own-cta" className="mt-8 bg-tl-purpleLight/60 border border-tl-purple/30 rounded-tlLg p-5 text-center">
              <p className="text-[14px] text-slate-800">Want a story for YOUR child?</p>
              <Link to="/create" className="inline-flex items-center gap-1.5 mt-2 text-tl-purple font-semibold text-[13px] hover:underline">
                Create one free at TalesLore <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {/* Post-unlock actions */}
          {unlocked && (
            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="inline-flex items-center gap-2 text-tl-success text-[12px]">
                <CheckCircle2 className="h-4 w-4" /> Story unlocked · enjoy bedtime
              </div>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <Button data-testid="story-download-pdf-button" onClick={doDownloadPdf} className="h-11 bg-tl-purple hover:bg-tl-purpleDark text-white text-[13px] gap-2">
                  <Download className="h-4 w-4" /> Download PDF
                </Button>
                <Button
                  data-testid="story-whatsapp-share-post"
                  onClick={doWhatsApp}
                  className="h-11 bg-[#25D366] hover:bg-[#1EBE5B] text-white text-[13px] gap-2"
                >
                  <Share2 className="h-4 w-4" /> Share on WhatsApp
                </Button>
                <Button data-testid="story-regenerate-same-child-button" onClick={doRegenerate} disabled={regenerating} variant="outline" className="h-11 border-slate-300 text-slate-800 hover:bg-slate-50 text-[13px] gap-2">
                  <RotateCcw className="h-4 w-4" /> {regenerating ? 'Weaving…' : 'Same child, new story'}
                </Button>
                <Button data-testid="story-new-story-button" onClick={() => navigate('/create')} variant="outline" className="h-11 border-slate-300 text-slate-800 hover:bg-slate-50 text-[13px] gap-2">
                  <Plus className="h-4 w-4" /> New story
                </Button>
              </div>
              <p className="mt-4 text-[11px] text-slate-500">
                Persistent story link: <a href={shareUrl} className="text-tl-purple hover:underline" data-testid="story-share-url">{shareUrl}</a>
              </p>
            </div>
          )}
        </div>
      </section>

      <EmailCaptureModal
        open={emailModalOpen}
        onOpenChange={setEmailModalOpen}
        priceDisplay={priceLoading ? '…' : pricing.single.display}
        childName={input?.child_name}
        deityName={input?.deity_name}
        onSubmit={doCheckoutWithLead}
        submitting={paying}
      />
    </div>
  );
}
